// @flow
import _ from 'lodash';

import ko from 'rgs-knockout';
import kv from 'rgs-knockout-validation';

import { observableISODate } from 'observable-iso-date';
import { observableJSableMobilePhone } from 'observable-mobile-phone';
import { observableDocumentType } from 'observable-document-type';
import ObservableMap from 'observable-map';
import MapOfObservables from 'map-of-observables';
import { documentRepr } from 'ko-extender-repr-document-type';

import {
	JSableOmitPrivate,
	createJSableEntityListMapExtender,
} from 'jsable';

import type {
	ProgramPremiumInterface,
	ProgramInsuranceInterface,
	RiskInterface,
	ProgramInterface,
	CalculationResultInterface,
	CalculationResultDiscountsInterface,
	QuotationDiscountOfferInterface,
	CalculationResultDiscountInterface,
	QuotationResultsInterface,
	QuotationPolicySelectedProgramSelectedRiskInterface,
	QuotationPolicySelectedProgramInterface,
	QuotationPolicyInterface,
	QuotationPhysicalPersonInterface,
	QuotationDiscountClaimsInterface,
	QuotationInterface,
	InsurancePeriodInterface,
	PersonIdentityDocumentInterface,
	InsurantAgreementsInterface,
	QuotationDeliveryInterface,
	OrderInterface,
	PeriodModelInterface,
	FileModelInterface,
	DocumentInterface,
	IRestIdentityDocument,
	RestIdentityDocumentOptions,
} from '../contracts.flow';

import { Address, Country } from './address';

export * from './address';

export class AbstractInsurantAgreements
	extends JSableOmitPrivate
	implements InsurantAgreementsInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.IsProcessedForSubscribe = ko.observable()
			.extend({ toFromJS: true });
	}
}


export class AbstractProgramInsurance
	extends JSableOmitPrivate
	implements ProgramInsuranceInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Insurance = ko.observable()
			.extend({ toFromJS: true });

		this.Currency = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class AbstractProgramPremium
	extends JSableOmitPrivate
	implements ProgramPremiumInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Premium = ko.observable()
			.extend({ toFromJS: true });

		this.PremiumBeforeDiscount = ko.observable()
			.extend({ toFromJS: true });

		this.DiscountCoeff = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class AbstractRisk
	extends JSableOmitPrivate
	implements RiskInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.Insurance = new AbstractProgramInsurance(...args);

		this.Premium = new AbstractProgramPremium(...args);
	}
}

export class AbstractProgram
	extends JSableOmitPrivate
	implements ProgramInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.Insurance = new AbstractProgramInsurance(...args);

		this.Premium = new AbstractProgramPremium(...args);

		this.AvailableRisks = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(AbstractRisk, [...args]));
	}
}

export class QuotationDiscountOffer
	extends JSableOmitPrivate
	implements QuotationDiscountOfferInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.OfferKey = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class CalculationResultDiscount
	extends JSableOmitPrivate
	implements CalculationResultDiscountInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Type = ko.observable()
			.extend({ toFromJS: true });

		this.DiscountCoeff = ko.observable()
			.extend({ toFromJS: true });

		this.Offer = new QuotationDiscountOffer(...args);
	}
}

export class CalculationResultDiscounts
	extends JSableOmitPrivate
	implements CalculationResultDiscountsInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.List = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(CalculationResultDiscount, [...args]));
	}
}

export class AbstractCalculationResult
	extends JSableOmitPrivate
	implements CalculationResultInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Premium = ko.observable()
			.extend({ toFromJS: true });

		this.PremiumBeforeDiscount = ko.observable()
			.extend({ toFromJS: true });

		this.DiscountCoeff = ko.observable()
			.extend({ toFromJS: true });

		this.Discounts = new CalculationResultDiscount(...args);

		this.AvailablePrograms = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(AbstractProgram, [...args]));
	}
}

export class AbstractQuotationResults
	extends JSableOmitPrivate
	implements QuotationResultsInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Calculation = new AbstractCalculationResult(...args);
	}
}

export class AbstractQuotationPolicySelectedProgramSelectedRisk
	extends JSableOmitPrivate
	implements QuotationPolicySelectedProgramSelectedRiskInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class AbstractQuotationPolicySelectedProgram
	extends JSableOmitPrivate
	implements QuotationPolicySelectedProgramInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.SelectedRisks = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(
				AbstractQuotationPolicySelectedProgramSelectedRisk, [...args]),
			);
	}
}

export class Document
	extends JSableOmitPrivate
	implements DocumentInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Type = ko.observable()
			.extend({ toFromJS: true });

		this.Number = ko.observable()
			.extend({ toFromJS: true });
	
		this.Series = ko.observable()
			.extend({ toFromJS: true });

		this.Country = new Country();

		this.isForeign = ko.map(ko.observable(false),
			{
				mapWrite: (v) => {
					if (!v) {
						this.Country.ID(null);
						this.Country.Name(null);
					}
					return v;
				},
			});

		this.IssueDate = ko.observable()
			.extend({ toFromJS: true });
	}

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}
}

export class RestIdentityDocument
	extends JSableOmitPrivate
	implements IRestIdentityDocument {
	constructor (options: RestIdentityDocumentOptions) {
		super(options);
		options = _.defaultsDeep({}, options, {
			dictionaries: {
				documentTypes: {},
				kladr: {
					countries: new ObservableMap(),
				},
			},
		});

		this._countriesDictionary = options.dictionaries.countries;

		this.Type = observableDocumentType()
			.extend({ repr: documentRepr(options.dictionaries.documentTypes) })
			.extend({ toFromJS: true });

		this._valueByDocumentType = new MapOfObservables();
		this._seriesByDocumentType = new MapOfObservables();
		this._numberByDocumentType = new MapOfObservables();

		// FRAGILE: no backward mapping 'Series'+'Number' into 'singleSeriesNumberInput'
		this.singleSeriesNumberInput = ko.pureComputed({
			read: () => {
				const type = this.Type();
				const value = this._valueByDocumentType.get(type);
				return value();
			},
			write: (v) => {
				const part1 = v.split(' ').slice(0, 1).join();
				const part2 = v.split(' ').slice(1).join(' ');

				if (!part2) {
					this.Series(null);
					this.Number(part1);
				} else {
					this.Series(part1);
					this.Number(part2);
				}

				const type = this.Type();
				const value = this._valueByDocumentType.get(type);
				value(v);
			},
		});

		this.Number = ko.pureComputed({
			read: () => {
				const type = this.Type();
				const number = this._numberByDocumentType.get(type);
				return number();
			},
			write: v => {
				const type = this.Type();
				const number = this._numberByDocumentType.get(type);
				number(v);
			},
		})
			.extend({ toFromJS: true });
	
		this.Series = ko.pureComputed({
			read: () => {
				const type = this.Type();
				const series = this._seriesByDocumentType.get(type);
				return series();
			},
			write: v => {
				const type = this.Type();
				const series = this._seriesByDocumentType.get(type);
				series(v);
			},
		})
			.extend({ toFromJS: true });

		this.IssueCountryID = ko.map(ko.observable(), {
			mapRead: (v) => {
				if (!this.Type.isDocumentForeignPassport()) {
					return null;
				}

				return v;
			},
		})
			.extend({ toFromJS: true });

		this.IssueCountryName = ko.pureComputed({
			read: () => {
				const issueCountryID = this.IssueCountryID();
				if (!issueCountryID) {
					return null;
				}

				return this._countriesDictionary.get(issueCountryID);
			},
			write: name => {
				if (!name) {
					this.IssueCountryID(null);
				} else {
					const id = this._countriesDictionary.get(name);
					if (id) {
						this.IssueCountryID(id);
					}
				}
			},
		})
			.extend({ toFromJS: true });
	}

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}

	fromJS (js: *, ...rest: Array<any>) {
		if (js && js.Type) {
			this.Type(js.Type);
		}

		if (js && js.IssueCountryID && js.IssueCountryName) {
			this._countriesDictionary.bisetAbsent(
				js.IssueCountryID,
				js.IssueCountryName,
				true
			);

			this._countriesDictionary.notify();
		}

		if (js && js.Type && js.Number) {
			if (js.Series) {
				this.singleSeriesNumberInput(`${js.Series} ${js.Number}`);
			} else {
				this.singleSeriesNumberInput(js.Number);
			}
		}

		return super.fromJS(js, ...rest);
	}
}

export class PersonIdentityDocument
	extends JSableOmitPrivate
	implements PersonIdentityDocumentInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Type = observableDocumentType()
			.extend({ toFromJS: true });

		this.Value = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class QuotationPhysicalPerson
	extends JSableOmitPrivate
	implements QuotationPhysicalPersonInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.FirstName = ko.observable()
			.extend({ toFromJS: true });

		this.LastName = ko.observable()
			.extend({ toFromJS: true });

		this.MiddleName = ko.observable()
			.extend({ toFromJS: true });

		this.BirthDay = observableISODate()
			.extend({ toFromJS: true });

		this.Phone = observableJSableMobilePhone();

		this.Email = ko.observable()
			.extend({ toFromJS: true });

		this.Registration = new Address(...args);

		this.IdentityDocument = new PersonIdentityDocument(...args);
	}
}

export class ClientSource
	extends JSableOmitPrivate {
	constructor (options: any) {
		super();

		this._options = _.defaults({}, options, {
			query: {}, // as returned by `querystring.parse()`
		});

		this.Query = ko.constant(_.flatten(_.map(this._options.query, (v, k: string) => {
			if (_.isArray(v)) { // multiple values for one key
				return _.map(v, v => ({ Key: k, Value: v }));
			}
			return [{ Key: k, Value: v }];
		})))
			.extend({ toJS: true });
	}
}

export class QuotationDiscountClaims
	extends JSableOmitPrivate
	implements QuotationDiscountClaimsInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Offer = new QuotationDiscountOffer(...args);

		this.Promocode = ko.observable()
			.extend({ toFromJS: true });

		this.IsWelcomePromotionRejected = ko.observable()
			.extend({ fromJS: true });

		this.ClientSource = new ClientSource(...args);
	}
}

export class AbstractQuotationPolicy
	extends JSableOmitPrivate
	implements QuotationPolicyInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Series = ko.observable()
			.extend({ toFromJS: true });

		this.Number = ko.observable()
			.extend({ toFromJS: true });

		this.StartDate = observableISODate()
			.extend({ toFromJS: true });

		this.EndDate = observableISODate()
			.extend({ toFromJS: true });

		this.InsurerType = ko.observable()
			.extend({ toFromJS: true });

		this.Insurer = ko.observable()
			.extend({ toFromJS: true });

		this.InsurantAgreements = new AbstractInsurantAgreements(...args);
	}
}

export class QuotationDelivery
	extends JSableOmitPrivate
	implements QuotationDeliveryInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.Type = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class AbstractQuotation
	extends JSableOmitPrivate
	implements QuotationInterface {
	constructor (options: any, ...args: Array<*>) {
		super(options, ...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.UserProfileID = ko.observable(_.get(options, ['userProfileJS', 'UserProfileID']))
			.extend({ toFromJS: true });

		this.InsuranceProductType = ko.observable()
			.extend({ toFromJS: true });

		this.Status = ko.observable()
			.extend({ toFromJS: true });

		this.Entities = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(QuotationPhysicalPerson, [...args]));

		this.Policy = new AbstractQuotationPolicy(...args);

		this.Delivery = new QuotationDelivery(...args);

		this.DiscountClaims = new QuotationDiscountClaims(...args);

		this.Results = new AbstractQuotationResults(...args);

		this.Context = ko.observable()
			.extend({ toFromJS: true });
	}
}

export class InsurancePeriod
	extends JSableOmitPrivate
	implements InsurancePeriodInterface {
	constructor (...args: Array<*>) {
		super(...args);

		this.StartDate = observableISODate()
			.extend({ toFromJS: true });

		this.EndDate = observableISODate()
			.extend({ toFromJS: true });
	}
}

export class AbstractQuotationVehicleDocument
	extends JSableOmitPrivate {
	constructor (...args: any) {
		super(...args);

		this.Number = ko.observable()
			.extend({ toFromJS: true });

		this.DateOfIssue = observableISODate()
			.extend({ toFromJS: true });
	}
}

export class AbstractQuotationVehicle
	extends JSableOmitPrivate {
	constructor (...args: any) {
		super(...args);

		this.ID = ko.observable()
			.extend({ toFromJS: true });

		this.ProductionYear = ko.observable()
			.extend({ toFromJS: true });

		this.UsagePurposeId = ko.observable()
			.extend({ toFromJS: true });

		this.IdentifierType = ko.observable()
			.extend({ toFromJS: true });

		this.BodyIdentifier = ko.observable()
			.extend({ toFromJS: true });

		this.ChassisIdentifier = ko.observable()
			.extend({ toFromJS: true });

		this.Vin = ko.observable()
			.extend({ toFromJS: true });

		this.HasRegistration = ko.observable()
			.extend({ toFromJS: true });

		this.RegNumber = ko.observable()
			.extend({ toFromJS: true });

		this.RegNumberIsForeign = ko.observable(false)
			.extend({ toFromJS: true });

		this.Certificate = new AbstractQuotationVehicleDocument(...args);

		this.Passport = new AbstractQuotationVehicleDocument(...args);
	}
}

export class FileModel
extends JSableOmitPrivate
implements FileModelInterface {
	constructor (...args: any) {
		super(...args);

		this.FileID = ko.observable()
			.extend({
				fromJS: true,
			});

		this.DataSize = ko.observable()
			.extend({
				fromJS: true,
			});

		this.FileName = ko.observable()
			.extend({
				fromJS: true,
			});
	}
}

export class PeriodModel
extends JSableOmitPrivate
implements PeriodModelInterface {
	constructor (...args: any) {
		super(...args);

		this.StartDate = observableISODate()
			.extend({
				fromJS: true,
			});

		this.EndDate = observableISODate()
			.extend({
				fromJS: true,
			});
	}
}

export class Order
extends JSableOmitPrivate
implements OrderInterface {
	constructor (...args: any) {
		super(...args);

		this.OrderCreatedWhen = observableISODate()
			.extend({
				fromJS: true,
			});

		this.QuotationCaseCreatedWhen = observableISODate()
			.extend({
				fromJS: true,
			});

		this.InsuranceProductType = ko.observable()
			.extend({
				fromJS: true,
			});

		this.InsurancePeriod = new PeriodModel();

		this.InsurancePeriodBeginDate = observableISODate()
			.extend({
				fromJS: true,
			});

		this.InsurancePeriodEndDate = observableISODate()
			.extend({
				fromJS: true,
			});

		this.Summary = ko.observable()
			.extend({
				fromJS: true,
			});

		this.IsEditable = ko.observable()
			.extend({
				fromJS: true,
			});

		this.IsObsolete = ko.observable()
			.extend({
				fromJS: true,
			});

		this.IsPaperPolicy = ko.observable()
			.extend({
				fromJS: true,
			});

		this.IsAutomaticProlongation = ko.observable()
			.extend({
				fromJS: true,
			});

		this.AutomaticProlongationQuotationCaseID = ko.observable()
			.extend({
				fromJS: true,
			});

		this.ProlongationKey = ko.observable()
			.extend({
				fromJS: true,
			});

		this.BsoDeliveryPrice = ko.observable()
			.extend({
				fromJS: true,
			});

		this.InsuranceApplicationFile = new FileModel();

		this.PolicyProjectFile = new FileModel();

		this.ElectronicSignatureFile = new FileModel();

		this.OrderPaymentStatus = ko.observable()
			.extend({
				fromJS: true,
			});

		this.OrderPolicyStatus = ko.observable()
			.extend({
				fromJS: true,
			});

		this.OrderID = ko.observable()
			.extend({
				fromJS: true,
			});

		this.OrderKey = ko.observable()
			.extend({
				fromJS: true,
			});

		this.PolicySeriesAndNumber = ko.observable()
			.extend({
				fromJS: true,
			});

		this.PolicyNumber = ko.observable()
			.extend({
				fromJS: true,
			});

		this.QuotationCaseID = ko.observable()
			.extend({
				fromJS: true,
			});

		this.PassbookKey = ko.observable()
			.extend({
				fromJS: true,
			});

		this.Premium = ko.observable()
			.extend({
				fromJS: true,
			});

		this.IsNewPolicyForUpgrade = ko.observable()
			.extend({
				fromJS: true,
			});
	}
}

